import React from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Label,
    LabelList,
} from "recharts";
import Icon from "../../assets/icons/Icon";
import HeatIcon from '../../assets/icons/heat.svg'
import CoolIcon from '../../assets/icons/cool.svg'
import { ENERGY_UNIT, formatConvertedEnergyValue } from "../../utils/energyUnit";
import { COOLING_LAST_YEAR_LABEL, HEATING_LAST_YEAR_LABEL } from "../../utils/lastYearComparison";

const RuntimeBarChart = ({
    data,
    energyUnit = ENERGY_UNIT.KWH,
    energyUnitLabel = "kWh",
    valuePrefix = "",
    appendEnergyUnitLabel = true,
    secondaryValuePrefix = "",
    showSecondaryValue = false,
    showLastYear = false,
    "data-testid": dataTestId = "runtime-bar-chart",
}) => {
    const valueLabelSuffix = appendEnergyUnitLabel ? ` ${energyUnitLabel}` : "";
    const getValueDisplay = (value, entry) => (
        `${valuePrefix}${formatConvertedEnergyValue(value, energyUnit)}${valueLabelSuffix}${
            showSecondaryValue && Number.isFinite(entry?.cost)
                ? ` (${secondaryValuePrefix}${Number(entry.cost).toFixed(2)})`
                : ""
        }`
    );

    // Custom label renderers for placing values above the bars
    const renderHeatingLabel = (props) => {
        const { x, y, width, value, payload } = props;
        const displayValue = payload?.valueLabel ?? (
            value != null
                ? getValueDisplay(value, payload)
                : payload?.value != null
                    ? getValueDisplay(payload.value, payload)
                    : null
        );
        if (displayValue == null) return null; // Skip if undefined or null
        return (
            <text
                x={x + width + 2}
                y={y + 5}
                fill="#FF8743"
                fontSize={12}
                textAnchor="start"
            >
                {displayValue}
            </text>
        );
    };

    const renderCoolingLabel = (props) => {
        const { x, y, width, value, payload } = props;
        const displayValue = payload?.coolingLabel ?? (
            value != null
                ? getValueDisplay(value, payload)
                : payload?.cooling != null
                    ? getValueDisplay(payload.cooling, payload)
                    : null
        );
        if (displayValue == null) return null; // Skip if undefined or null
        return (
            <text
                x={x + width}
                y={y - 15}
                fill="#4AC4F4"
                fontSize={12}
                textAnchor="start"
            >
                {displayValue}
            </text>
        );
    };

    const CustomHeatingBar = (props) => {
        const { x, y, width, height, fill } = props;
        const offset = 10; // pixels to shift downward
        return (
            <rect x={x} y={y + offset} width={width} height={height} fill={fill} />
        );
    };

    const CustomCoolingBar = ({ x, y, width, height, fill }) => {
        const offset = 10; // pixels upward
        return <rect x={x} y={y - offset} width={width} height={height} fill={fill} />;
    };

    const renderCompareLabel = (color, labelKey) => (props) => {
        const { x, y, width, height, index } = props;
        const displayValue = data[index]?.[labelKey];
        if (displayValue == null) return null;
        return (
            <text
                x={x + width + 4}
                y={y + height / 2 + 4}
                fill={color}
                fontSize={12}
                textAnchor="start"
            >
                {displayValue}
            </text>
        );
    };

    const maxValue = Math.max(
        ...data.map((item) => Math.max(
            item.value || 0,
            item.cooling || 0,
            item.lastYearValue || 0,
            item.lastYearCooling || 0,
        ))
    );

    const finalMax = maxValue > 0 ? Number((maxValue * 1.25).toFixed(2)) : 1;

    const ticks = [];
    const tickStep = finalMax <= 1 ? 0.2 : finalMax <= 5 ? 1 : Math.ceil(finalMax / 5);
    for (let i = 0; i <= finalMax + tickStep / 2; i += tickStep) {
        ticks.push(Number(i.toFixed(2)));
    }

    const CustomTooltip = ({ active, payload }) => {
        if (!active || !payload || !payload.length) return null;

        return (
            <div className="rounded-lg px-4 py-3 shadow-md text-xs font-semibold text-white bg-[#1E555C]">
                {payload.map((item) => (
                    <div key={item.dataKey} className="flex justify-between">
                        <span className="mr-2">{item.name}:</span>
                        <span>{getValueDisplay(item.value, item.payload)}</span>
                    </div>
                ))}
            </div>
        );
    };


    return (
        <div className="bg-white dark:bg-transparent w-full" data-testid={dataTestId}>
            <ResponsiveContainer width="100%" height={showLastYear ? 260 : 150}>
                <BarChart
                    layout="vertical"
                    data={data}
                    margin={{ top: 20, right: 20, bottom: 0, left: 0 }}
                >
                    <XAxis
                        type="number"
                        domain={[0, finalMax]}
                        tick={{ fontSize: 8, fill: "currentcolor", className: "text-black dark:text-white" }}
                        ticks={ticks}
                        tickFormatter={(value) => formatConvertedEnergyValue(value, energyUnit)}
                        axisLine={false}
                        tickLine={false}
                    >
                        <Label
                            value={valuePrefix || energyUnitLabel}
                            className="text-black dark:text-white"
                            position="insideLeft"
                            offset={-50}
                            dy={-4}
                            style={{ textAnchor: "start", fontSize: 8, fill: "currentcolor" }}
                        />
                    </XAxis>
                    <YAxis
                        type="category"
                        dataKey="label"
                        interval={0}
                        width={100}
                        axisLine={{ stroke: "#c5c8c9" }}
                        tickLine={false}
                        tick={({ x, y, payload }) => {
                            const isHeating = payload.value === "Heating";
                            const isCooling = payload.value === "Cooling";
                            const isLastYear = payload.value === HEATING_LAST_YEAR_LABEL
                                || payload.value === COOLING_LAST_YEAR_LABEL;

                            return (
                                <g transform={`translate(${x}, ${y})`}>
                                    {isHeating && (
                                        <foreignObject x={-80} y={-12} width={24} height={24}>
                                            <Icon src={HeatIcon} alt="HeatIcon" className="w-5 h-5" />
                                        </foreignObject>
                                    )}
                                    {isCooling && (
                                        <foreignObject x={-80} y={-12} width={24} height={24}>
                                            <Icon src={CoolIcon} alt="CoolIcon" className="w-5 h-5" />
                                        </foreignObject>
                                    )}
                                    <text className="text-black dark:text-white" x={-50} y={-2} dy={5} fontSize={12} fill="currentColor" opacity={isLastYear ? 0.6 : 1}>
                                        {isLastYear ? <title>{payload.value}</title> : null}
                                        {isLastYear ? "Last year" : payload.value}
                                    </text>
                                </g>
                            );
                        }}
                    />
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: "rgba(0,0,0,0.05)" }}
                    />
                    <Bar
                        dataKey="value"
                        fill="#FF8743"
                        name="Heating"
                        barSize={15}
                        stackId={showLastYear ? "usage" : undefined}
                        shape={showLastYear ? undefined : <CustomHeatingBar />}
                    >
                        <LabelList content={showLastYear ? renderCompareLabel("#FF8743", "valueLabel") : renderHeatingLabel} />
                    </Bar>
                    <Icon src={HeatIcon} alt="HeatIcon" className="w-6 h-6" />
                    <Bar
                        dataKey="cooling"
                        fill="#4AC4F4"
                        name="Cooling"
                        barSize={15}
                        stackId={showLastYear ? "usage" : undefined}
                        shape={showLastYear ? undefined : <CustomCoolingBar />}
                    >
                        <LabelList content={showLastYear ? renderCompareLabel("#4AC4F4", "coolingLabel") : renderCoolingLabel} />
                    </Bar>
                    {showLastYear ? (
                        <Bar
                            dataKey="lastYearValue"
                            fill="#FFC9A8"
                            name="Heating (last year)"
                            barSize={15}
                            stackId="usage"
                        >
                            <LabelList content={renderCompareLabel("#C2733A", "lastYearValueLabel")} />
                        </Bar>
                    ) : null}
                    {showLastYear ? (
                        <Bar
                            dataKey="lastYearCooling"
                            fill="#B3E4F9"
                            name="Cooling (last year)"
                            barSize={15}
                            stackId="usage"
                        >
                            <LabelList content={renderCompareLabel("#2E8FB8", "lastYearCoolingLabel")} />
                        </Bar>
                    ) : null}

                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default RuntimeBarChart;
